// Transcript repair: the repair panel, the quality report and the inline edit popover (PLAN-
// ADDENDUM A3).
//
// Namespace `meetings-repair`; owner WP-8 (BUILD 1). Only the owner adds keys or
// translations. Placeholders are `{name}`, filled with `formatCopy`. Counts are
// written as "label: {count}" so no language needs plural forms.
// See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  repairTitle: 'Transcript check',
  repairRunning: 'Checking the transcript against a second engine and company knowledge…',
  repairFailed: 'The transcript check could not finish. The transcript is unchanged.',
  repairSkipped: 'This transcript was not cross-checked by a second engine.',
  repairCurrent: 'Transcript checked.',
  appliedCount: 'Corrections applied: {count}',
  suggestedCount: 'Suggestions to review: {count}',
  noSuggestions: 'No corrections needed.',
  reviewSuggestions: 'Review suggestions',
  hideSuggestions: 'Hide suggestions',
  autoApplied: 'Applied automatically',
  suggestion: 'Suggestion',
  heard: 'Heard',
  corrected: 'Corrected',
  reason: 'Why',
  evidence: 'Evidence',
  evidenceEngine: 'Second engine: “{text}”',
  evidenceVocabulary: 'Company vocabulary: {term}',
  evidenceKnowledge: 'Company knowledge: “{excerpt}”',
  evidenceContext: 'Context: “{excerpt}”',
  confidence: '{percent} % confident',
  accept: 'Accept',
  reject: 'Reject',
  undo: 'Undo',
  accepted: 'Accepted',
  rejected: 'Rejected',
  undone: 'Undone',
  showInTranscript: 'Show in transcript',
  actionFailed: 'The change could not be saved. Reload the meeting and try again.',
  safeChangesOnly: 'Numbers, dates and negations are never changed automatically.',
  notesRegenerating: 'Corrections saved · refreshing notes',
  kindProperNoun: 'Name',
  kindTerm: 'Term',
  kindNumber: 'Number',
  kindDate: 'Date',
  kindNegation: 'Negation',
  kindWording: 'Wording',
  kindOther: 'Other',
  qualityTitle: 'Transcript quality',
  provenance: 'Words from {words}. Speakers from {speakers}.',
  provenanceWords: 'Words from {words}.',
  coverageComplete: 'Every five-minute window of the audio has words.',
  coverageFlagged:
    'Five-minute windows without words: {count}. Listen to them before relying on the transcript.',
  coverageWindow: 'No words from {start} to {end}',
  repeatedPhrases: 'Repeated phrases flagged as possible hallucinations: {count}.',
  uncertainSpans: 'Uncertain passages, marked and not guessed: {count}.',
  uncertain: 'Uncertain',
  uncertainDisagree: 'The engines disagree here',
  uncertainLowConfidence: 'Too unsure to suggest a correction',
  uncertainLong: 'Too long a disagreement to check',
  uncertainUnchecked: 'Not checked',
  disagreements: 'Passages where the two engines disagreed: {count}.',
};

export type MeetingsRepairCopyKey = keyof typeof en;

const meetingsRepairCopy: NamespaceCatalog<MeetingsRepairCopyKey> = {
  bg: {
    repairTitle: 'Проверка на транскрипцията',
    repairRunning: 'Транскрипцията се проверява срещу втора система и знанията на компанията…',
    repairFailed: 'Проверката на транскрипцията не можа да завърши. Транскрипцията е непроменена.',
    repairSkipped: 'Тази транскрипция не е проверена с втора система.',
    repairCurrent: 'Транскрипцията е проверена.',
    appliedCount: 'Приложени корекции: {count}',
    suggestedCount: 'Предложения за преглед: {count}',
    noSuggestions: 'Не са нужни корекции.',
    reviewSuggestions: 'Прегледай предложенията',
    hideSuggestions: 'Скрий предложенията',
    autoApplied: 'Приложено автоматично',
    suggestion: 'Предложение',
    heard: 'Чуто',
    corrected: 'Коригирано',
    reason: 'Защо',
    evidence: 'Доказателства',
    evidenceEngine: 'Втора система: „{text}“',
    evidenceVocabulary: 'Речник на компанията: {term}',
    evidenceKnowledge: 'Знания на компанията: „{excerpt}“',
    evidenceContext: 'Контекст: „{excerpt}“',
    confidence: '{percent} % увереност',
    accept: 'Приеми',
    reject: 'Отхвърли',
    undo: 'Отмени',
    accepted: 'Прието',
    rejected: 'Отхвърлено',
    undone: 'Отменено',
    showInTranscript: 'Покажи в транскрипцията',
    actionFailed: 'Промяната не можа да бъде запазена. Презаредете срещата и опитайте отново.',
    safeChangesOnly: 'Числа, дати и отрицания никога не се променят автоматично.',
    notesRegenerating: 'Корекциите са запазени · бележките се обновяват',
    kindProperNoun: 'Име',
    kindTerm: 'Термин',
    kindNumber: 'Число',
    kindDate: 'Дата',
    kindNegation: 'Отрицание',
    kindWording: 'Формулировка',
    kindOther: 'Друго',
    qualityTitle: 'Качество на транскрипцията',
    provenance: 'Думи от {words}. Говорители от {speakers}.',
    provenanceWords: 'Думи от {words}.',
    coverageComplete: 'Всеки петминутен интервал от записа съдържа думи.',
    coverageFlagged:
      'Петминутни интервали без думи: {count}. Изслушайте ги, преди да разчитате на транскрипцията.',
    coverageWindow: 'Няма думи от {start} до {end}',
    repeatedPhrases: 'Повтарящи се фрази, отбелязани като възможни халюцинации: {count}.',
    uncertainSpans: 'Несигурни пасажи са отбелязани, а не отгатнати: {count}.',
    uncertain: 'Несигурно',
    uncertainDisagree: 'Системите не са съгласни тук',
    uncertainLowConfidence: 'Твърде несигурно, за да се предложи корекция',
    uncertainLong: 'Твърде дълго разминаване за проверка',
    uncertainUnchecked: 'Непроверено',
    disagreements: 'Пасажи, в които двете системи се разминаха: {count}.',
  },
  hr: {
    repairTitle: 'Provjera transkripta',
    repairRunning: 'Provjeravam transkript pomoću drugog sustava i znanja tvrtke…',
    repairFailed: 'Provjera transkripta nije dovršena. Transkript je nepromijenjen.',
    repairSkipped: 'Ovaj transkript nije provjeren drugim sustavom.',
    repairCurrent: 'Transkript je provjeren.',
    appliedCount: 'Primijenjeno ispravaka: {count}',
    suggestedCount: 'Prijedloga za pregled: {count}',
    noSuggestions: 'Ispravci nisu potrebni.',
    reviewSuggestions: 'Pregledaj prijedloge',
    hideSuggestions: 'Sakrij prijedloge',
    autoApplied: 'Primijenjeno automatski',
    suggestion: 'Prijedlog',
    heard: 'Čuto',
    corrected: 'Ispravljeno',
    reason: 'Zašto',
    evidence: 'Dokazi',
    evidenceEngine: 'Drugi sustav: „{text}”',
    evidenceVocabulary: 'Rječnik tvrtke: {term}',
    evidenceKnowledge: 'Znanje tvrtke: „{excerpt}”',
    evidenceContext: 'Kontekst: „{excerpt}”',
    confidence: 'Sigurnost {percent} %',
    accept: 'Prihvati',
    reject: 'Odbij',
    undo: 'Poništi',
    accepted: 'Prihvaćeno',
    rejected: 'Odbijeno',
    undone: 'Poništeno',
    showInTranscript: 'Prikaži u transkriptu',
    actionFailed: 'Promjena nije spremljena. Ponovno učitajte sastanak i pokušajte opet.',
    safeChangesOnly: 'Brojevi, datumi i negacije nikad se ne mijenjaju automatski.',
    notesRegenerating: 'Ispravci su spremljeni · bilješke se osvježavaju',
    kindProperNoun: 'Ime',
    kindTerm: 'Pojam',
    kindNumber: 'Broj',
    kindDate: 'Datum',
    kindNegation: 'Negacija',
    kindWording: 'Formulacija',
    kindOther: 'Ostalo',
    qualityTitle: 'Kvaliteta transkripta',
    provenance: 'Riječi iz {words}. Govornici iz {speakers}.',
    provenanceWords: 'Riječi iz {words}.',
    coverageComplete: 'Svaki petominutni odsječak snimke sadrži riječi.',
    coverageFlagged:
      'Petominutnih odsječaka bez riječi: {count}. Poslušajte ih prije nego što se oslonite na transkript.',
    coverageWindow: 'Nema riječi od {start} do {end}',
    repeatedPhrases: 'Ponovljenih fraza označenih kao moguće halucinacije: {count}.',
    uncertainSpans: 'Nesigurnih odlomaka označeno, a ne pogođeno: {count}.',
    uncertain: 'Nesigurno',
    uncertainDisagree: 'Sustavi se ovdje ne slažu',
    uncertainLowConfidence: 'Previše nesigurno za prijedlog ispravka',
    uncertainLong: 'Predugo neslaganje za provjeru',
    uncertainUnchecked: 'Nije provjereno',
    disagreements: 'Odlomci u kojima su se sustavi razišli: {count}.',
  },
  cs: {
    repairTitle: 'Kontrola přepisu',
    repairRunning: 'Kontroluji přepis pomocí druhého systému a znalostí firmy…',
    repairFailed: 'Kontrolu přepisu se nepodařilo dokončit. Přepis zůstal beze změny.',
    repairSkipped: 'Tento přepis nebyl ověřen druhým systémem.',
    repairCurrent: 'Přepis je zkontrolován.',
    appliedCount: 'Použité opravy: {count}',
    suggestedCount: 'Návrhy ke kontrole: {count}',
    noSuggestions: 'Opravy nejsou potřeba.',
    reviewSuggestions: 'Zkontrolovat návrhy',
    hideSuggestions: 'Skrýt návrhy',
    autoApplied: 'Použito automaticky',
    suggestion: 'Návrh',
    heard: 'Slyšeno',
    corrected: 'Opraveno',
    reason: 'Proč',
    evidence: 'Důkazy',
    evidenceEngine: 'Druhý systém: „{text}“',
    evidenceVocabulary: 'Firemní slovník: {term}',
    evidenceKnowledge: 'Znalosti firmy: „{excerpt}“',
    evidenceContext: 'Kontext: „{excerpt}“',
    confidence: 'Jistota {percent} %',
    accept: 'Přijmout',
    reject: 'Odmítnout',
    undo: 'Vrátit',
    accepted: 'Přijato',
    rejected: 'Odmítnuto',
    undone: 'Vráceno',
    showInTranscript: 'Zobrazit v přepisu',
    actionFailed: 'Změnu se nepodařilo uložit. Znovu načtěte schůzku a zkuste to znovu.',
    safeChangesOnly: 'Čísla, data a zápory se nikdy nemění automaticky.',
    notesRegenerating: 'Opravy uloženy · poznámky se obnovují',
    kindProperNoun: 'Jméno',
    kindTerm: 'Termín',
    kindNumber: 'Číslo',
    kindDate: 'Datum',
    kindNegation: 'Zápor',
    kindWording: 'Formulace',
    kindOther: 'Jiné',
    qualityTitle: 'Kvalita přepisu',
    provenance: 'Slova z {words}. Mluvčí z {speakers}.',
    provenanceWords: 'Slova z {words}.',
    coverageComplete: 'Každý pětiminutový úsek nahrávky obsahuje slova.',
    coverageFlagged:
      'Pětiminutové úseky bez slov: {count}. Poslechněte si je, než se na přepis spolehnete.',
    coverageWindow: 'Žádná slova od {start} do {end}',
    repeatedPhrases: 'Opakované fráze označené jako možné halucinace: {count}.',
    uncertainSpans: 'Nejisté pasáže jsou označeny, ne hádány: {count}.',
    uncertain: 'Nejisté',
    uncertainDisagree: 'Systémy se zde neshodují',
    uncertainLowConfidence: 'Příliš nejisté pro návrh opravy',
    uncertainLong: 'Příliš dlouhý rozpor na kontrolu',
    uncertainUnchecked: 'Nezkontrolováno',
    disagreements: 'Pasáže, ve kterých se systémy rozcházely: {count}.',
  },
  da: {
    repairTitle: 'Kontrol af transskription',
    repairRunning: 'Kontrollerer transskriptionen mod en anden motor og virksomhedens viden…',
    repairFailed:
      'Kontrollen af transskriptionen kunne ikke gøres færdig. Transskriptionen er uændret.',
    repairSkipped: 'Denne transskription er ikke krydstjekket af en anden motor.',
    repairCurrent: 'Transskriptionen er kontrolleret.',
    appliedCount: 'Rettelser anvendt: {count}',
    suggestedCount: 'Forslag til gennemsyn: {count}',
    noSuggestions: 'Ingen rettelser nødvendige.',
    reviewSuggestions: 'Gennemse forslag',
    hideSuggestions: 'Skjul forslag',
    autoApplied: 'Anvendt automatisk',
    suggestion: 'Forslag',
    heard: 'Hørt',
    corrected: 'Rettet',
    reason: 'Hvorfor',
    evidence: 'Belæg',
    evidenceEngine: 'Anden motor: “{text}”',
    evidenceVocabulary: 'Virksomhedens ordforråd: {term}',
    evidenceKnowledge: 'Virksomhedens viden: “{excerpt}”',
    evidenceContext: 'Kontekst: “{excerpt}”',
    confidence: '{percent} % sikker',
    accept: 'Godkend',
    reject: 'Afvis',
    undo: 'Fortryd',
    accepted: 'Godkendt',
    rejected: 'Afvist',
    undone: 'Fortrudt',
    showInTranscript: 'Vis i transskriptionen',
    actionFailed: 'Ændringen kunne ikke gemmes. Genindlæs mødet, og prøv igen.',
    safeChangesOnly: 'Tal, datoer og negationer ændres aldrig automatisk.',
    notesRegenerating: 'Rettelser gemt · noterne opdateres',
    kindProperNoun: 'Navn',
    kindTerm: 'Fagudtryk',
    kindNumber: 'Tal',
    kindDate: 'Dato',
    kindNegation: 'Nægtelse',
    kindWording: 'Formulering',
    kindOther: 'Andet',
    qualityTitle: 'Transskriptionens kvalitet',
    provenance: 'Ord fra {words}. Talere fra {speakers}.',
    provenanceWords: 'Ord fra {words}.',
    coverageComplete: 'Hvert femminutters vindue af lyden har ord.',
    coverageFlagged:
      'Femminutters vinduer uden ord: {count}. Lyt til dem, før du stoler på transskriptionen.',
    coverageWindow: 'Ingen ord fra {start} til {end}',
    repeatedPhrases: 'Gentagne fraser markeret som mulige hallucinationer: {count}.',
    uncertainSpans: 'Usikre passager, markeret og ikke gættet: {count}.',
    uncertain: 'Usikker',
    uncertainDisagree: 'Motorerne er uenige her',
    uncertainLowConfidence: 'For usikkert til at foreslå en rettelse',
    uncertainLong: 'For lang uenighed til at kontrollere',
    uncertainUnchecked: 'Ikke kontrolleret',
    disagreements: 'Passager, hvor de to motorer var uenige: {count}.',
  },
  nl: {
    repairTitle: 'Transcriptcontrole',
    repairRunning: 'Het transcript wordt gecontroleerd met een tweede engine en bedrijfskennis…',
    repairFailed:
      'De controle van het transcript kon niet worden voltooid. Het transcript is ongewijzigd.',
    repairSkipped: 'Dit transcript is niet gecontroleerd door een tweede engine.',
    repairCurrent: 'Transcript gecontroleerd.',
    appliedCount: 'Toegepaste correcties: {count}',
    suggestedCount: 'Te beoordelen suggesties: {count}',
    noSuggestions: 'Geen correcties nodig.',
    reviewSuggestions: 'Suggesties beoordelen',
    hideSuggestions: 'Suggesties verbergen',
    autoApplied: 'Automatisch toegepast',
    suggestion: 'Suggestie',
    heard: 'Gehoord',
    corrected: 'Gecorrigeerd',
    reason: 'Waarom',
    evidence: 'Onderbouwing',
    evidenceEngine: 'Tweede engine: ‘{text}’',
    evidenceVocabulary: 'Bedrijfswoordenlijst: {term}',
    evidenceKnowledge: 'Bedrijfskennis: ‘{excerpt}’',
    evidenceContext: 'Context: ‘{excerpt}’',
    confidence: '{percent}% zeker',
    accept: 'Accepteren',
    reject: 'Afwijzen',
    undo: 'Ongedaan maken',
    accepted: 'Geaccepteerd',
    rejected: 'Afgewezen',
    undone: 'Ongedaan gemaakt',
    showInTranscript: 'Tonen in transcript',
    actionFailed:
      'De wijziging kon niet worden opgeslagen. Laad de vergadering opnieuw en probeer het nog eens.',
    safeChangesOnly: 'Getallen, datums en ontkenningen worden nooit automatisch gewijzigd.',
    notesRegenerating: 'Correcties opgeslagen · notities worden vernieuwd',
    kindProperNoun: 'Naam',
    kindTerm: 'Vakterm',
    kindNumber: 'Getal',
    kindDate: 'Datum',
    kindNegation: 'Ontkenning',
    kindWording: 'Formulering',
    kindOther: 'Overig',
    qualityTitle: 'Kwaliteit van het transcript',
    provenance: 'Woorden van {words}. Sprekers van {speakers}.',
    provenanceWords: 'Woorden van {words}.',
    coverageComplete: 'Elk venster van vijf minuten in de opname bevat woorden.',
    coverageFlagged:
      'Vensters van vijf minuten zonder woorden: {count}. Luister ze terug voordat u op het transcript vertrouwt.',
    coverageWindow: 'Geen woorden van {start} tot {end}',
    repeatedPhrases: 'Herhaalde zinsdelen gemarkeerd als mogelijke hallucinaties: {count}.',
    uncertainSpans: 'Onzekere passages, gemarkeerd en niet geraden: {count}.',
    uncertain: 'Onzeker',
    uncertainDisagree: 'De engines zijn het hier oneens',
    uncertainLowConfidence: 'Te onzeker om een correctie voor te stellen',
    uncertainLong: 'Te lange onenigheid om te controleren',
    uncertainUnchecked: 'Niet gecontroleerd',
    disagreements: 'Passages waarin de twee engines het oneens waren: {count}.',
  },
  en,
  et: {
    repairTitle: 'Transkriptsiooni kontroll',
    repairRunning: 'Kontrollin transkriptsiooni teise tuvastusmootori ja ettevõtte teadmiste abil…',
    repairFailed: 'Transkriptsiooni kontrolli ei õnnestunud lõpetada. Transkriptsioon on muutmata.',
    repairSkipped: 'Seda transkriptsiooni ei ole teise mootoriga kontrollitud.',
    repairCurrent: 'Transkriptsioon on kontrollitud.',
    appliedCount: 'Rakendatud parandusi: {count}',
    suggestedCount: 'Ülevaatamist ootavaid soovitusi: {count}',
    noSuggestions: 'Parandusi pole vaja.',
    reviewSuggestions: 'Vaata soovitused üle',
    hideSuggestions: 'Peida soovitused',
    autoApplied: 'Rakendatud automaatselt',
    suggestion: 'Soovitus',
    heard: 'Kuuldud',
    corrected: 'Parandatud',
    reason: 'Miks',
    evidence: 'Tõendid',
    evidenceEngine: 'Teine mootor: „{text}”',
    evidenceVocabulary: 'Ettevõtte sõnavara: {term}',
    evidenceKnowledge: 'Ettevõtte teadmised: „{excerpt}”',
    evidenceContext: 'Kontekst: „{excerpt}”',
    confidence: '{percent}% kindel',
    accept: 'Nõustu',
    reject: 'Lükka tagasi',
    undo: 'Võta tagasi',
    accepted: 'Nõustutud',
    rejected: 'Tagasi lükatud',
    undone: 'Tagasi võetud',
    showInTranscript: 'Näita transkriptsioonis',
    actionFailed: 'Muudatust ei õnnestunud salvestada. Laadi koosolek uuesti ja proovi uuesti.',
    safeChangesOnly: 'Numbreid, kuupäevi ja eitusi ei muudeta kunagi automaatselt.',
    notesRegenerating: 'Parandused salvestatud · märkmeid värskendatakse',
    kindProperNoun: 'Nimi',
    kindTerm: 'Termin',
    kindNumber: 'Arv',
    kindDate: 'Kuupäev',
    kindNegation: 'Eitus',
    kindWording: 'Sõnastus',
    kindOther: 'Muu',
    qualityTitle: 'Transkriptsiooni kvaliteet',
    provenance: 'Sõnad: {words}. Kõnelejad: {speakers}.',
    provenanceWords: 'Sõnad: {words}.',
    coverageComplete: 'Salvestuse igas viieminutilises lõigus on sõnu.',
    coverageFlagged:
      'Sõnadeta viieminutilisi lõike: {count}. Kuula need üle, enne kui transkriptsiooni usaldad.',
    coverageWindow: 'Sõnu pole vahemikus {start}–{end}',
    repeatedPhrases: 'Võimalike hallutsinatsioonidena märgitud korduvaid fraase: {count}.',
    uncertainSpans: 'Ebakindlaid lõike on märgitud, mitte oletatud: {count}.',
    uncertain: 'Ebakindel',
    uncertainDisagree: 'Mootorid on siin eri meelt',
    uncertainLowConfidence: 'Paranduse soovitamiseks liiga ebakindel',
    uncertainLong: 'Kontrollimiseks liiga pikk lahknevus',
    uncertainUnchecked: 'Kontrollimata',
    disagreements: 'Lõigud, kus mootorid olid eri meelt: {count}.',
  },
  fi: {
    repairTitle: 'Litteroinnin tarkistus',
    repairRunning: 'Tarkistetaan litterointia toisella tunnistimella ja yrityksen tiedoilla…',
    repairFailed: 'Litteroinnin tarkistusta ei voitu viedä loppuun. Litterointi on ennallaan.',
    repairSkipped: 'Tätä litterointia ei ole tarkistettu toisella tunnistimella.',
    repairCurrent: 'Litterointi tarkistettu.',
    appliedCount: 'Korjauksia tehty: {count}',
    suggestedCount: 'Tarkistettavia ehdotuksia: {count}',
    noSuggestions: 'Korjauksia ei tarvita.',
    reviewSuggestions: 'Käy ehdotukset läpi',
    hideSuggestions: 'Piilota ehdotukset',
    autoApplied: 'Tehty automaattisesti',
    suggestion: 'Ehdotus',
    heard: 'Kuultu',
    corrected: 'Korjattu',
    reason: 'Miksi',
    evidence: 'Perusteet',
    evidenceEngine: 'Toinen tunnistin: ”{text}”',
    evidenceVocabulary: 'Yrityksen sanasto: {term}',
    evidenceKnowledge: 'Yrityksen tieto: ”{excerpt}”',
    evidenceContext: 'Asiayhteys: ”{excerpt}”',
    confidence: '{percent} % varma',
    accept: 'Hyväksy',
    reject: 'Hylkää',
    undo: 'Kumoa',
    accepted: 'Hyväksytty',
    rejected: 'Hylätty',
    undone: 'Kumottu',
    showInTranscript: 'Näytä litteroinnissa',
    actionFailed: 'Muutosta ei voitu tallentaa. Lataa kokous uudelleen ja yritä uudelleen.',
    safeChangesOnly: 'Numeroita, päivämääriä ja kieltoja ei koskaan muuteta automaattisesti.',
    notesRegenerating: 'Korjaukset tallennettu · muistiinpanoja päivitetään',
    kindProperNoun: 'Nimi',
    kindTerm: 'Termi',
    kindNumber: 'Luku',
    kindDate: 'Päivämäärä',
    kindNegation: 'Kielto',
    kindWording: 'Sanamuoto',
    kindOther: 'Muu',
    qualityTitle: 'Litteroinnin laatu',
    provenance: 'Sanat: {words}. Puhujat: {speakers}.',
    provenanceWords: 'Sanat: {words}.',
    coverageComplete: 'Tallenteen jokaisessa viiden minuutin jaksossa on sanoja.',
    coverageFlagged:
      'Viiden minuutin jaksoja ilman sanoja: {count}. Kuuntele ne ennen kuin luotat litterointiin.',
    coverageWindow: 'Ei sanoja välillä {start}–{end}',
    repeatedPhrases: 'Mahdollisiksi hallusinaatioiksi merkittyjä toistuvia fraaseja: {count}.',
    uncertainSpans: 'Epävarmoja kohtia merkitty arvaamatta: {count}.',
    uncertain: 'Epävarma',
    uncertainDisagree: 'Tunnistimet ovat tästä eri mieltä',
    uncertainLowConfidence: 'Liian epävarma korjausehdotukseen',
    uncertainLong: 'Liian pitkä eroavuus tarkistettavaksi',
    uncertainUnchecked: 'Tarkistamatta',
    disagreements: 'Kohdat, joissa tunnistimet olivat eri mieltä: {count}.',
  },
  fr: {
    repairTitle: 'Vérification de la transcription',
    repairRunning:
      'Vérification de la transcription avec un second moteur et les connaissances de l’entreprise…',
    repairFailed:
      'La vérification de la transcription n’a pas pu aboutir. La transcription est inchangée.',
    repairSkipped: 'Cette transcription n’a pas été recoupée par un second moteur.',
    repairCurrent: 'Transcription vérifiée.',
    appliedCount: 'Corrections appliquées : {count}',
    suggestedCount: 'Suggestions à examiner : {count}',
    noSuggestions: 'Aucune correction nécessaire.',
    reviewSuggestions: 'Examiner les suggestions',
    hideSuggestions: 'Masquer les suggestions',
    autoApplied: 'Appliqué automatiquement',
    suggestion: 'Proposition',
    heard: 'Entendu',
    corrected: 'Corrigé',
    reason: 'Pourquoi',
    evidence: 'Éléments',
    evidenceEngine: 'Second moteur : « {text} »',
    evidenceVocabulary: 'Vocabulaire de l’entreprise : {term}',
    evidenceKnowledge: 'Connaissances de l’entreprise : « {excerpt} »',
    evidenceContext: 'Contexte : « {excerpt} »',
    confidence: 'Confiance : {percent} %',
    accept: 'Accepter',
    reject: 'Refuser',
    undo: 'Annuler',
    accepted: 'Accepté',
    rejected: 'Refusé',
    undone: 'Annulé',
    showInTranscript: 'Afficher dans la transcription',
    actionFailed: 'La modification n’a pas pu être enregistrée. Rechargez la réunion et réessayez.',
    safeChangesOnly:
      'Les nombres, les dates et les négations ne sont jamais modifiés automatiquement.',
    notesRegenerating: 'Corrections enregistrées · actualisation des notes',
    kindProperNoun: 'Nom',
    kindTerm: 'Terme',
    kindNumber: 'Nombre',
    kindDate: 'Date',
    kindNegation: 'Négation',
    kindWording: 'Formulation',
    kindOther: 'Autre',
    qualityTitle: 'Qualité de la transcription',
    provenance: 'Mots issus de {words}. Intervenants issus de {speakers}.',
    provenanceWords: 'Mots issus de {words}.',
    coverageComplete: 'Chaque tranche de cinq minutes de l’enregistrement contient des mots.',
    coverageFlagged:
      'Tranches de cinq minutes sans aucun mot : {count}. Écoutez-les avant de vous fier à la transcription.',
    coverageWindow: 'Aucun mot de {start} à {end}',
    repeatedPhrases: 'Phrases répétées signalées comme hallucinations possibles : {count}.',
    uncertainSpans: 'Passages incertains, signalés et non devinés : {count}.',
    uncertain: 'Incertain',
    uncertainDisagree: 'Les moteurs divergent ici',
    uncertainLowConfidence: 'Trop incertain pour proposer une correction',
    uncertainLong: 'Divergence trop longue pour être vérifiée',
    uncertainUnchecked: 'Non vérifié',
    disagreements: 'Passages où les deux moteurs ont divergé : {count}.',
  },
  de: {
    repairTitle: 'Transkriptprüfung',
    repairRunning: 'Das Transkript wird mit einer zweiten Engine und dem Firmenwissen abgeglichen…',
    repairFailed:
      'Die Transkriptprüfung konnte nicht abgeschlossen werden. Das Transkript ist unverändert.',
    repairSkipped: 'Dieses Transkript wurde nicht mit einer zweiten Engine abgeglichen.',
    repairCurrent: 'Transkript geprüft.',
    appliedCount: 'Übernommene Korrekturen: {count}',
    suggestedCount: 'Zu prüfende Vorschläge: {count}',
    noSuggestions: 'Keine Korrekturen nötig.',
    reviewSuggestions: 'Vorschläge prüfen',
    hideSuggestions: 'Vorschläge ausblenden',
    autoApplied: 'Automatisch übernommen',
    suggestion: 'Vorschlag',
    heard: 'Gehört',
    corrected: 'Korrigiert',
    reason: 'Warum',
    evidence: 'Belege',
    evidenceEngine: 'Zweite Engine: „{text}“',
    evidenceVocabulary: 'Firmenvokabular: {term}',
    evidenceKnowledge: 'Firmenwissen: „{excerpt}“',
    evidenceContext: 'Kontext: „{excerpt}“',
    confidence: '{percent} % sicher',
    accept: 'Übernehmen',
    reject: 'Ablehnen',
    undo: 'Rückgängig',
    accepted: 'Übernommen',
    rejected: 'Abgelehnt',
    undone: 'Rückgängig gemacht',
    showInTranscript: 'Im Transkript zeigen',
    actionFailed:
      'Die Änderung konnte nicht gespeichert werden. Laden Sie das Meeting neu und versuchen Sie es erneut.',
    safeChangesOnly: 'Zahlen, Daten und Verneinungen werden nie automatisch geändert.',
    notesRegenerating: 'Korrekturen gespeichert · Notizen werden aktualisiert',
    kindProperNoun: 'Eigenname',
    kindTerm: 'Fachbegriff',
    kindNumber: 'Zahl',
    kindDate: 'Datum',
    kindNegation: 'Verneinung',
    kindWording: 'Formulierung',
    kindOther: 'Sonstiges',
    qualityTitle: 'Transkriptqualität',
    provenance: 'Wörter von {words}. Sprecher von {speakers}.',
    provenanceWords: 'Wörter von {words}.',
    coverageComplete: 'Jedes Fünf-Minuten-Fenster der Aufnahme enthält Wörter.',
    coverageFlagged:
      'Fünf-Minuten-Fenster ohne Wörter: {count}. Hören Sie sie an, bevor Sie sich auf das Transkript verlassen.',
    coverageWindow: 'Keine Wörter von {start} bis {end}',
    repeatedPhrases: 'Wiederholte Wendungen, als mögliche Halluzinationen markiert: {count}.',
    uncertainSpans: 'Unsichere Stellen, markiert und nicht geraten: {count}.',
    uncertain: 'Unsicher',
    uncertainDisagree: 'Die Engines sind sich hier uneinig',
    uncertainLowConfidence: 'Zu unsicher für einen Korrekturvorschlag',
    uncertainLong: 'Zu lange Abweichung für eine Prüfung',
    uncertainUnchecked: 'Nicht geprüft',
    disagreements: 'Stellen, an denen die beiden Engines voneinander abwichen: {count}.',
  },
  el: {
    repairTitle: 'Έλεγχος απομαγνητοφώνησης',
    repairRunning:
      'Γίνεται έλεγχος της απομαγνητοφώνησης με δεύτερη μηχανή και τη γνώση της εταιρείας…',
    repairFailed:
      'Ο έλεγχος της απομαγνητοφώνησης δεν ολοκληρώθηκε. Η απομαγνητοφώνηση δεν άλλαξε.',
    repairSkipped: 'Αυτή η απομαγνητοφώνηση δεν διασταυρώθηκε με δεύτερη μηχανή.',
    repairCurrent: 'Η απομαγνητοφώνηση ελέγχθηκε.',
    appliedCount: 'Διορθώσεις που εφαρμόστηκαν: {count}',
    suggestedCount: 'Προτάσεις για έλεγχο: {count}',
    noSuggestions: 'Δεν χρειάζονται διορθώσεις.',
    reviewSuggestions: 'Έλεγχος προτάσεων',
    hideSuggestions: 'Απόκρυψη προτάσεων',
    autoApplied: 'Εφαρμόστηκε αυτόματα',
    suggestion: 'Πρόταση',
    heard: 'Ακούστηκε',
    corrected: 'Διορθώθηκε',
    reason: 'Γιατί',
    evidence: 'Τεκμήρια',
    evidenceEngine: 'Δεύτερη μηχανή: «{text}»',
    evidenceVocabulary: 'Λεξιλόγιο εταιρείας: {term}',
    evidenceKnowledge: 'Γνώση εταιρείας: «{excerpt}»',
    evidenceContext: 'Συμφραζόμενα: «{excerpt}»',
    confidence: 'Βεβαιότητα {percent} %',
    accept: 'Αποδοχή',
    reject: 'Απόρριψη',
    undo: 'Αναίρεση',
    accepted: 'Έγινε αποδοχή',
    rejected: 'Απορρίφθηκε',
    undone: 'Αναιρέθηκε',
    showInTranscript: 'Εμφάνιση στην απομαγνητοφώνηση',
    actionFailed: 'Η αλλαγή δεν αποθηκεύτηκε. Φορτώστε ξανά τη σύσκεψη και δοκιμάστε πάλι.',
    safeChangesOnly: 'Αριθμοί, ημερομηνίες και αρνήσεις δεν αλλάζουν ποτέ αυτόματα.',
    notesRegenerating: 'Οι διορθώσεις αποθηκεύτηκαν · οι σημειώσεις ανανεώνονται',
    kindProperNoun: 'Όνομα',
    kindTerm: 'Όρος',
    kindNumber: 'Αριθμός',
    kindDate: 'Ημερομηνία',
    kindNegation: 'Άρνηση',
    kindWording: 'Διατύπωση',
    kindOther: 'Άλλο',
    qualityTitle: 'Ποιότητα απομαγνητοφώνησης',
    provenance: 'Λέξεις από {words}. Ομιλητές από {speakers}.',
    provenanceWords: 'Λέξεις από {words}.',
    coverageComplete: 'Κάθε πεντάλεπτο διάστημα της ηχογράφησης έχει λέξεις.',
    coverageFlagged:
      'Πεντάλεπτα διαστήματα χωρίς λέξεις: {count}. Ακούστε τα πριν βασιστείτε στην απομαγνητοφώνηση.',
    coverageWindow: 'Καμία λέξη από {start} έως {end}',
    repeatedPhrases: 'Επαναλαμβανόμενες φράσεις που σημειώθηκαν ως πιθανές ψευδαισθήσεις: {count}.',
    uncertainSpans: 'Αβέβαια αποσπάσματα σημειώνονται, δεν μαντεύονται: {count}.',
    uncertain: 'Αβέβαιο',
    uncertainDisagree: 'Οι μηχανές διαφωνούν εδώ',
    uncertainLowConfidence: 'Πολύ αβέβαιο για πρόταση διόρθωσης',
    uncertainLong: 'Πολύ μεγάλη διαφωνία για έλεγχο',
    uncertainUnchecked: 'Δεν ελέγχθηκε',
    disagreements: 'Αποσπάσματα όπου οι δύο μηχανές διαφώνησαν: {count}.',
  },
  hu: {
    repairTitle: 'Átirat-ellenőrzés',
    repairRunning: 'Az átirat ellenőrzése egy második motorral és a cég tudásával…',
    repairFailed: 'Az átirat ellenőrzése nem fejeződött be. Az átirat változatlan.',
    repairSkipped: 'Ezt az átiratot nem ellenőrizte második motor.',
    repairCurrent: 'Az átirat ellenőrizve.',
    appliedCount: 'Alkalmazott javítások: {count}',
    suggestedCount: 'Átnézendő javaslatok: {count}',
    noSuggestions: 'Nincs szükség javításra.',
    reviewSuggestions: 'Javaslatok átnézése',
    hideSuggestions: 'Javaslatok elrejtése',
    autoApplied: 'Automatikusan alkalmazva',
    suggestion: 'Javaslat',
    heard: 'Elhangzott',
    corrected: 'Javítva',
    reason: 'Miért',
    evidence: 'Bizonyítékok',
    evidenceEngine: 'Második motor: „{text}”',
    evidenceVocabulary: 'Céges szókincs: {term}',
    evidenceKnowledge: 'Céges tudás: „{excerpt}”',
    evidenceContext: 'Kontextus: „{excerpt}”',
    confidence: '{percent}%-os bizonyosság',
    accept: 'Elfogadás',
    reject: 'Elutasítás',
    undo: 'Visszavonás',
    accepted: 'Elfogadva',
    rejected: 'Elutasítva',
    undone: 'Visszavonva',
    showInTranscript: 'Megjelenítés az átiratban',
    actionFailed:
      'A módosítást nem sikerült menteni. Töltse be újra a megbeszélést, és próbálja újra.',
    safeChangesOnly:
      'Számokat, dátumokat és tagadásokat a rendszer soha nem módosít automatikusan.',
    notesRegenerating: 'Javítások mentve · a jegyzetek frissülnek',
    kindProperNoun: 'Név',
    kindTerm: 'Szakkifejezés',
    kindNumber: 'Szám',
    kindDate: 'Dátum',
    kindNegation: 'Tagadás',
    kindWording: 'Megfogalmazás',
    kindOther: 'Egyéb',
    qualityTitle: 'Az átirat minősége',
    provenance: 'Szavak forrása: {words}. Beszélők forrása: {speakers}.',
    provenanceWords: 'Szavak forrása: {words}.',
    coverageComplete: 'A felvétel minden ötperces szakaszában vannak szavak.',
    coverageFlagged:
      'Szavak nélküli ötperces szakaszok: {count}. Hallgassa meg őket, mielőtt az átiratra hagyatkozik.',
    coverageWindow: 'Nincsenek szavak {start} és {end} között',
    repeatedPhrases: 'Lehetséges hallucinációként megjelölt ismétlődő kifejezések: {count}.',
    uncertainSpans: 'Megjelölt, nem kitalált bizonytalan részek: {count}.',
    uncertain: 'Bizonytalan',
    uncertainDisagree: 'A motorok itt nem értenek egyet',
    uncertainLowConfidence: 'Túl bizonytalan a javításhoz',
    uncertainLong: 'Túl hosszú eltérés az ellenőrzéshez',
    uncertainUnchecked: 'Nincs ellenőrizve',
    disagreements: 'Részletek, ahol a két motor eltért egymástól: {count}.',
  },
  ga: {
    repairTitle: 'Seiceáil tras-scríbhinne',
    repairRunning:
      'Tá an tras-scríbhinn á seiceáil i gcoinne dara hinneall agus eolas na cuideachta…',
    repairFailed:
      'Níorbh fhéidir an tras-scríbhinn a sheiceáil go hiomlán. Níl aon athrú ar an tras-scríbhinn.',
    repairSkipped: 'Níor seiceáladh an tras-scríbhinn seo le dara hinneall.',
    repairCurrent: 'Tras-scríbhinn seiceáilte.',
    appliedCount: 'Ceartúcháin curtha i bhfeidhm: {count}',
    suggestedCount: 'Moltaí le hathbhreithniú: {count}',
    noSuggestions: 'Níl gá le ceartúcháin.',
    reviewSuggestions: 'Athbhreithnigh na moltaí',
    hideSuggestions: 'Folaigh na moltaí',
    autoApplied: 'Curtha i bhfeidhm go huathoibríoch',
    suggestion: 'Moladh',
    heard: 'Cloiste',
    corrected: 'Ceartaithe',
    reason: 'Cén fáth',
    evidence: 'Fianaise',
    evidenceEngine: 'Dara hinneall: “{text}”',
    evidenceVocabulary: 'Stór focal na cuideachta: {term}',
    evidenceKnowledge: 'Eolas na cuideachta: “{excerpt}”',
    evidenceContext: 'Comhthéacs: “{excerpt}”',
    confidence: '{percent} % cinnte',
    accept: 'Glac leis',
    reject: 'Diúltaigh',
    undo: 'Cealaigh',
    accepted: 'Glactha',
    rejected: 'Diúltaithe',
    undone: 'Cealaithe',
    showInTranscript: 'Taispeáin sa tras-scríbhinn',
    actionFailed:
      'Níorbh fhéidir an t-athrú a shábháil. Athlódáil an cruinniú agus bain triail eile as.',
    safeChangesOnly: 'Ní athraítear uimhreacha, dátaí ná diúltuithe go huathoibríoch riamh.',
    notesRegenerating: 'Ceartúcháin sábháilte · na nótaí á n-athnuachan',
    kindProperNoun: 'Ainm',
    kindTerm: 'Téarma',
    kindNumber: 'Uimhir',
    kindDate: 'Dáta',
    kindNegation: 'Diúltú',
    kindWording: 'Foclaíocht',
    kindOther: 'Eile',
    qualityTitle: 'Cáilíocht na tras-scríbhinne',
    provenance: 'Focail ó {words}. Cainteoirí ó {speakers}.',
    provenanceWords: 'Focail ó {words}.',
    coverageComplete: 'Tá focail i ngach fuinneog cúig nóiméad den taifeadadh.',
    coverageFlagged:
      'Fuinneoga cúig nóiméad gan aon fhocal: {count}. Éist leo sula mbraitheann tú ar an tras-scríbhinn.',
    coverageWindow: 'Gan focail ó {start} go {end}',
    repeatedPhrases: 'Frásaí athráite marcáilte mar shiabhráin fhéideartha: {count}.',
    uncertainSpans: 'Sleachta éiginnte marcáilte, gan buille faoi thuairim: {count}.',
    uncertain: 'Éiginnte',
    uncertainDisagree: 'Níl na hinnill ar aon fhocal anseo',
    uncertainLowConfidence: 'Ró-éiginnte le ceartúchán a mholadh',
    uncertainLong: 'Easaontas rófhada le seiceáil',
    uncertainUnchecked: 'Gan seiceáil',
    disagreements: 'Sleachta ina raibh an dá inneall ar easaontas: {count}.',
  },
  it: {
    repairTitle: 'Controllo della trascrizione',
    repairRunning: 'Controllo della trascrizione con un secondo motore e le conoscenze aziendali…',
    repairFailed:
      'Non è stato possibile completare il controllo della trascrizione. La trascrizione è invariata.',
    repairSkipped: 'Questa trascrizione non è stata verificata con un secondo motore.',
    repairCurrent: 'Trascrizione controllata.',
    appliedCount: 'Correzioni applicate: {count}',
    suggestedCount: 'Suggerimenti da esaminare: {count}',
    noSuggestions: 'Nessuna correzione necessaria.',
    reviewSuggestions: 'Esamina i suggerimenti',
    hideSuggestions: 'Nascondi i suggerimenti',
    autoApplied: 'Applicata automaticamente',
    suggestion: 'Suggerimento',
    heard: 'Sentito',
    corrected: 'Corretto',
    reason: 'Perché',
    evidence: 'Prove',
    evidenceEngine: 'Secondo motore: «{text}»',
    evidenceVocabulary: 'Vocabolario aziendale: {term}',
    evidenceKnowledge: 'Conoscenze aziendali: «{excerpt}»',
    evidenceContext: 'Contesto: «{excerpt}»',
    confidence: 'Sicurezza {percent}%',
    accept: 'Accetta',
    reject: 'Rifiuta',
    undo: 'Annulla',
    accepted: 'Accettata',
    rejected: 'Rifiutata',
    undone: 'Annullata',
    showInTranscript: 'Mostra nella trascrizione',
    actionFailed: 'Impossibile salvare la modifica. Ricarica la riunione e riprova.',
    safeChangesOnly: 'Numeri, date e negazioni non vengono mai modificati automaticamente.',
    notesRegenerating: 'Correzioni salvate · aggiornamento delle note',
    kindProperNoun: 'Nome',
    kindTerm: 'Termine',
    kindNumber: 'Numero',
    kindDate: 'Data',
    kindNegation: 'Negazione',
    kindWording: 'Formulazione',
    kindOther: 'Altro',
    qualityTitle: 'Qualità della trascrizione',
    provenance: 'Parole da {words}. Interlocutori da {speakers}.',
    provenanceWords: 'Parole da {words}.',
    coverageComplete: 'Ogni finestra di cinque minuti della registrazione contiene parole.',
    coverageFlagged:
      'Finestre di cinque minuti senza parole: {count}. Ascoltale prima di fidarti della trascrizione.',
    coverageWindow: 'Nessuna parola da {start} a {end}',
    repeatedPhrases: 'Frasi ripetute segnalate come possibili allucinazioni: {count}.',
    uncertainSpans: 'Passaggi incerti, segnalati e non indovinati: {count}.',
    uncertain: 'Incerto',
    uncertainDisagree: 'I motori qui non concordano',
    uncertainLowConfidence: 'Troppo incerto per suggerire una correzione',
    uncertainLong: 'Divergenza troppo lunga da controllare',
    uncertainUnchecked: 'Non controllato',
    disagreements: 'Passaggi in cui i due motori hanno dato letture diverse: {count}.',
  },
  lv: {
    repairTitle: 'Transkripcijas pārbaude',
    repairRunning: 'Transkripcija tiek pārbaudīta ar otru dzinēju un uzņēmuma zināšanām…',
    repairFailed: 'Transkripcijas pārbaudi neizdevās pabeigt. Transkripcija nav mainīta.',
    repairSkipped: 'Šī transkripcija nav salīdzināta ar otru dzinēju.',
    repairCurrent: 'Transkripcija pārbaudīta.',
    appliedCount: 'Piemēroti labojumi: {count}',
    suggestedCount: 'Pārskatāmi ieteikumi: {count}',
    noSuggestions: 'Labojumi nav vajadzīgi.',
    reviewSuggestions: 'Pārskatīt ieteikumus',
    hideSuggestions: 'Paslēpt ieteikumus',
    autoApplied: 'Piemērots automātiski',
    suggestion: 'Ieteikums',
    heard: 'Sadzirdēts',
    corrected: 'Labots',
    reason: 'Kāpēc',
    evidence: 'Pierādījumi',
    evidenceEngine: 'Otrs dzinējs: “{text}”',
    evidenceVocabulary: 'Uzņēmuma vārdnīca: {term}',
    evidenceKnowledge: 'Uzņēmuma zināšanas: “{excerpt}”',
    evidenceContext: 'Konteksts: “{excerpt}”',
    confidence: '{percent} % pārliecība',
    accept: 'Pieņemt',
    reject: 'Noraidīt',
    undo: 'Atsaukt',
    accepted: 'Pieņemts',
    rejected: 'Noraidīts',
    undone: 'Atsaukts',
    showInTranscript: 'Rādīt transkripcijā',
    actionFailed: 'Izmaiņas neizdevās saglabāt. Pārlādējiet sapulci un mēģiniet vēlreiz.',
    safeChangesOnly: 'Skaitļi, datumi un noliegumi nekad netiek mainīti automātiski.',
    notesRegenerating: 'Labojumi saglabāti · piezīmes tiek atjauninātas',
    kindProperNoun: 'Vārds',
    kindTerm: 'Termins',
    kindNumber: 'Skaitlis',
    kindDate: 'Datums',
    kindNegation: 'Noliegums',
    kindWording: 'Formulējums',
    kindOther: 'Cits',
    qualityTitle: 'Transkripcijas kvalitāte',
    provenance: 'Vārdi no {words}. Runātāji no {speakers}.',
    provenanceWords: 'Vārdi no {words}.',
    coverageComplete: 'Katrā ieraksta piecu minūšu logā ir vārdi.',
    coverageFlagged:
      'Piecu minūšu logi bez vārdiem: {count}. Noklausieties tos, pirms paļaujaties uz transkripciju.',
    coverageWindow: 'Nav vārdu no {start} līdz {end}',
    repeatedPhrases: 'Atkārtotas frāzes, atzīmētas kā iespējamas halucinācijas: {count}.',
    uncertainSpans: 'Neskaidri fragmenti atzīmēti, nevis uzminēti: {count}.',
    uncertain: 'Neskaidrs',
    uncertainDisagree: 'Dzinēji šeit nav vienisprātis',
    uncertainLowConfidence: 'Pārāk neskaidrs, lai ieteiktu labojumu',
    uncertainLong: 'Pārāk gara atšķirība pārbaudei',
    uncertainUnchecked: 'Nav pārbaudīts',
    disagreements: 'Fragmenti, kuros abi dzinēji atšķīrās: {count}.',
  },
  lt: {
    repairTitle: 'Stenogramos patikra',
    repairRunning: 'Stenograma tikrinama su antru varikliu ir įmonės žiniomis…',
    repairFailed: 'Stenogramos patikros užbaigti nepavyko. Stenograma nepakeista.',
    repairSkipped: 'Ši stenograma nebuvo sutikrinta su antru varikliu.',
    repairCurrent: 'Stenograma patikrinta.',
    appliedCount: 'Pritaikyta pataisų: {count}',
    suggestedCount: 'Peržiūrėtinų pasiūlymų: {count}',
    noSuggestions: 'Pataisų nereikia.',
    reviewSuggestions: 'Peržiūrėti pasiūlymus',
    hideSuggestions: 'Slėpti pasiūlymus',
    autoApplied: 'Pritaikyta automatiškai',
    suggestion: 'Pasiūlymas',
    heard: 'Išgirsta',
    corrected: 'Pataisyta',
    reason: 'Kodėl',
    evidence: 'Įrodymai',
    evidenceEngine: 'Antras variklis: „{text}“',
    evidenceVocabulary: 'Įmonės žodynas: {term}',
    evidenceKnowledge: 'Įmonės žinios: „{excerpt}“',
    evidenceContext: 'Kontekstas: „{excerpt}“',
    confidence: '{percent} % tikrumas',
    accept: 'Priimti',
    reject: 'Atmesti',
    undo: 'Atšaukti',
    accepted: 'Priimta',
    rejected: 'Atmesta',
    undone: 'Atšaukta',
    showInTranscript: 'Rodyti stenogramoje',
    actionFailed:
      'Pakeitimo išsaugoti nepavyko. Iš naujo įkelkite susitikimą ir bandykite dar kartą.',
    safeChangesOnly: 'Skaičiai, datos ir neiginiai niekada nekeičiami automatiškai.',
    notesRegenerating: 'Pataisos išsaugotos · pastabos atnaujinamos',
    kindProperNoun: 'Vardas',
    kindTerm: 'Terminas',
    kindNumber: 'Skaičius',
    kindDate: 'Data',
    kindNegation: 'Neiginys',
    kindWording: 'Formuluotė',
    kindOther: 'Kita',
    qualityTitle: 'Stenogramos kokybė',
    provenance: 'Žodžiai iš {words}. Kalbėtojai iš {speakers}.',
    provenanceWords: 'Žodžiai iš {words}.',
    coverageComplete: 'Kiekviename penkių minučių įrašo lange yra žodžių.',
    coverageFlagged:
      'Penkių minučių langų be žodžių: {count}. Paklausykite jų prieš pasikliaudami stenograma.',
    coverageWindow: 'Nėra žodžių nuo {start} iki {end}',
    repeatedPhrases: 'Pasikartojančių frazių, pažymėtų kaip galimos haliucinacijos: {count}.',
    uncertainSpans: 'Neaiškios vietos pažymėtos, o ne atspėtos: {count}.',
    uncertain: 'Neaišku',
    uncertainDisagree: 'Varikliai čia nesutaria',
    uncertainLowConfidence: 'Per daug neaišku, kad būtų siūloma pataisa',
    uncertainLong: 'Per ilgas neatitikimas patikrai',
    uncertainUnchecked: 'Nepatikrinta',
    disagreements: 'Vietos, kuriose varikliai nesutarė: {count}.',
  },
  mt: {
    repairTitle: 'Verifika tat-traskrizzjoni',
    repairRunning: 'Qed nivverifika t-traskrizzjoni ma’ magna oħra u l-għarfien tal-kumpanija…',
    repairFailed:
      'Il-verifika tat-traskrizzjoni ma setgħetx titlesta. It-traskrizzjoni ma nbidlitx.',
    repairSkipped: 'Din it-traskrizzjoni ma ġietx ivverifikata b’magna oħra.',
    repairCurrent: 'It-traskrizzjoni ġiet ivverifikata.',
    appliedCount: 'Korrezzjonijiet applikati: {count}',
    suggestedCount: 'Suġġerimenti biex jiġu rieżaminati: {count}',
    noSuggestions: 'M’hemmx bżonn ta’ korrezzjonijiet.',
    reviewSuggestions: 'Irrieżamina s-suġġerimenti',
    hideSuggestions: 'Aħbi s-suġġerimenti',
    autoApplied: 'Applikat awtomatikament',
    suggestion: 'Suġġeriment',
    heard: 'Instema’',
    corrected: 'Ikkoreġut',
    reason: 'Għaliex',
    evidence: 'Evidenza',
    evidenceEngine: 'It-tieni magna: “{text}”',
    evidenceVocabulary: 'Vokabularju tal-kumpanija: {term}',
    evidenceKnowledge: 'Għarfien tal-kumpanija: “{excerpt}”',
    evidenceContext: 'Kuntest: “{excerpt}”',
    confidence: '{percent} % ċert',
    accept: 'Aċċetta',
    reject: 'Irrifjuta',
    undo: 'Neħħi',
    accepted: 'Aċċettat',
    rejected: 'Irrifjutat',
    undone: 'Imneħħi',
    showInTranscript: 'Uri fit-traskrizzjoni',
    actionFailed: 'Il-bidla ma setgħetx tiġi ssejvjata. Erġa’ tella’ l-laqgħa u erġa’ pprova.',
    safeChangesOnly: 'In-numri, id-dati u n-negazzjonijiet qatt ma jinbidlu awtomatikament.',
    notesRegenerating: 'Il-korrezzjonijiet ġew issejvjati · in-noti qed jiġu aġġornati',
    kindProperNoun: 'Isem',
    kindTerm: 'Terminu',
    kindNumber: 'Numru',
    kindDate: 'Data',
    kindNegation: 'Negazzjoni',
    kindWording: 'Kliem',
    kindOther: 'Oħrajn',
    qualityTitle: 'Kwalità tat-traskrizzjoni',
    provenance: 'Kliem minn {words}. Kelliema minn {speakers}.',
    provenanceWords: 'Kliem minn {words}.',
    coverageComplete: 'Kull tieqa ta’ ħames minuti tar-reġistrazzjoni fiha kliem.',
    coverageFlagged:
      'Twieqi ta’ ħames minuti mingħajr kliem: {count}. Isma’ minnhom qabel ma tistrieħ fuq it-traskrizzjoni.',
    coverageWindow: 'L-ebda kliem minn {start} sa {end}',
    repeatedPhrases: 'Frażijiet ripetuti mmarkati bħala alluċinazzjonijiet possibbli: {count}.',
    uncertainSpans: 'Siltiet inċerti mmarkati mingħajr konġetturi: {count}.',
    uncertain: 'Inċert',
    uncertainDisagree: 'Il-magni ma jaqblux hawn',
    uncertainLowConfidence: 'Inċert wisq biex tiġi ssuġġerita korrezzjoni',
    uncertainLong: 'Nuqqas ta’ qbil twil wisq biex jiġi vverifikat',
    uncertainUnchecked: 'Mhux ivverifikat',
    disagreements: 'Siltiet fejn iż-żewġ magni ma qablux: {count}.',
  },
  pl: {
    repairTitle: 'Kontrola transkrypcji',
    repairRunning: 'Sprawdzam transkrypcję za pomocą drugiego silnika i wiedzy firmy…',
    repairFailed:
      'Nie udało się dokończyć kontroli transkrypcji. Transkrypcja pozostała bez zmian.',
    repairSkipped: 'Ta transkrypcja nie została sprawdzona drugim silnikiem.',
    repairCurrent: 'Transkrypcja sprawdzona.',
    appliedCount: 'Zastosowane poprawki: {count}',
    suggestedCount: 'Sugestie do przejrzenia: {count}',
    noSuggestions: 'Poprawki nie są potrzebne.',
    reviewSuggestions: 'Przejrzyj sugestie',
    hideSuggestions: 'Ukryj sugestie',
    autoApplied: 'Zastosowano automatycznie',
    suggestion: 'Sugestia',
    heard: 'Usłyszano',
    corrected: 'Poprawiono',
    reason: 'Dlaczego',
    evidence: 'Dowody',
    evidenceEngine: 'Drugi silnik: „{text}”',
    evidenceVocabulary: 'Słownik firmy: {term}',
    evidenceKnowledge: 'Wiedza firmy: „{excerpt}”',
    evidenceContext: 'Kontekst: „{excerpt}”',
    confidence: 'Pewność {percent}%',
    accept: 'Akceptuj',
    reject: 'Odrzuć',
    undo: 'Cofnij',
    accepted: 'Zaakceptowano',
    rejected: 'Odrzucono',
    undone: 'Cofnięto',
    showInTranscript: 'Pokaż w transkrypcji',
    actionFailed: 'Nie udało się zapisać zmiany. Wczytaj spotkanie ponownie i spróbuj jeszcze raz.',
    safeChangesOnly: 'Liczby, daty i przeczenia nigdy nie są zmieniane automatycznie.',
    notesRegenerating: 'Poprawki zapisane · notatki są odświeżane',
    kindProperNoun: 'Nazwa',
    kindTerm: 'Termin',
    kindNumber: 'Liczba',
    kindDate: 'Data',
    kindNegation: 'Przeczenie',
    kindWording: 'Sformułowanie',
    kindOther: 'Inne',
    qualityTitle: 'Jakość transkrypcji',
    provenance: 'Słowa z {words}. Mówcy z {speakers}.',
    provenanceWords: 'Słowa z {words}.',
    coverageComplete: 'Każde pięciominutowe okno nagrania zawiera słowa.',
    coverageFlagged:
      'Pięciominutowe okna bez słów: {count}. Odsłuchaj je, zanim zaufasz transkrypcji.',
    coverageWindow: 'Brak słów od {start} do {end}',
    repeatedPhrases: 'Powtarzające się frazy oznaczone jako możliwe halucynacje: {count}.',
    uncertainSpans: 'Niepewne fragmenty są oznaczone, a nie zgadywane: {count}.',
    uncertain: 'Niepewne',
    uncertainDisagree: 'Silniki się tu nie zgadzają',
    uncertainLowConfidence: 'Zbyt niepewne, by zaproponować poprawkę',
    uncertainLong: 'Zbyt długa rozbieżność do sprawdzenia',
    uncertainUnchecked: 'Niesprawdzone',
    disagreements: 'Fragmenty, w których silniki się różniły: {count}.',
  },
  pt: {
    repairTitle: 'Verificação da transcrição',
    repairRunning: 'A verificar a transcrição com um segundo motor e o conhecimento da empresa…',
    repairFailed:
      'Não foi possível concluir a verificação da transcrição. A transcrição não foi alterada.',
    repairSkipped: 'Esta transcrição não foi verificada por um segundo motor.',
    repairCurrent: 'Transcrição verificada.',
    appliedCount: 'Correções aplicadas: {count}',
    suggestedCount: 'Sugestões para rever: {count}',
    noSuggestions: 'Não são necessárias correções.',
    reviewSuggestions: 'Rever sugestões',
    hideSuggestions: 'Ocultar sugestões',
    autoApplied: 'Aplicada automaticamente',
    suggestion: 'Sugestão',
    heard: 'Ouvido',
    corrected: 'Corrigido',
    reason: 'Porquê',
    evidence: 'Fundamentos',
    evidenceEngine: 'Segundo motor: «{text}»',
    evidenceVocabulary: 'Vocabulário da empresa: {term}',
    evidenceKnowledge: 'Conhecimento da empresa: «{excerpt}»',
    evidenceContext: 'Contexto: «{excerpt}»',
    confidence: '{percent}% de confiança',
    accept: 'Aceitar',
    reject: 'Rejeitar',
    undo: 'Anular',
    accepted: 'Aceite',
    rejected: 'Rejeitada',
    undone: 'Anulada',
    showInTranscript: 'Mostrar na transcrição',
    actionFailed: 'Não foi possível guardar a alteração. Recarregue a reunião e tente novamente.',
    safeChangesOnly: 'Números, datas e negações nunca são alterados automaticamente.',
    notesRegenerating: 'Correções guardadas · a atualizar as notas',
    kindProperNoun: 'Nome',
    kindTerm: 'Termo',
    kindNumber: 'Número',
    kindDate: 'Data',
    kindNegation: 'Negação',
    kindWording: 'Formulação',
    kindOther: 'Outro',
    qualityTitle: 'Qualidade da transcrição',
    provenance: 'Palavras de {words}. Oradores de {speakers}.',
    provenanceWords: 'Palavras de {words}.',
    coverageComplete: 'Cada janela de cinco minutos da gravação tem palavras.',
    coverageFlagged:
      'Janelas de cinco minutos sem palavras: {count}. Ouça-as antes de confiar na transcrição.',
    coverageWindow: 'Sem palavras de {start} a {end}',
    repeatedPhrases: 'Frases repetidas assinaladas como possíveis alucinações: {count}.',
    uncertainSpans: 'Passagens incertas, assinaladas e não adivinhadas: {count}.',
    uncertain: 'Incerto',
    uncertainDisagree: 'Os motores discordam aqui',
    uncertainLowConfidence: 'Demasiado incerto para sugerir uma correção',
    uncertainLong: 'Divergência demasiado longa para verificar',
    uncertainUnchecked: 'Não verificado',
    disagreements: 'Passagens em que os dois motores divergiram: {count}.',
  },
  ro: {
    repairTitle: 'Verificarea transcrierii',
    repairRunning: 'Se verifică transcrierea cu un al doilea motor și cu cunoștințele companiei…',
    repairFailed:
      'Verificarea transcrierii nu a putut fi finalizată. Transcrierea este neschimbată.',
    repairSkipped: 'Această transcriere nu a fost verificată de un al doilea motor.',
    repairCurrent: 'Transcriere verificată.',
    appliedCount: 'Corecturi aplicate: {count}',
    suggestedCount: 'Sugestii de revizuit: {count}',
    noSuggestions: 'Nu sunt necesare corecturi.',
    reviewSuggestions: 'Revizuiește sugestiile',
    hideSuggestions: 'Ascunde sugestiile',
    autoApplied: 'Aplicată automat',
    suggestion: 'Sugestie',
    heard: 'Auzit',
    corrected: 'Corectat',
    reason: 'De ce',
    evidence: 'Dovezi',
    evidenceEngine: 'Al doilea motor: „{text}”',
    evidenceVocabulary: 'Vocabularul companiei: {term}',
    evidenceKnowledge: 'Cunoștințele companiei: „{excerpt}”',
    evidenceContext: 'Context: „{excerpt}”',
    confidence: 'Încredere {percent}%',
    accept: 'Acceptă',
    reject: 'Respinge',
    undo: 'Anulează',
    accepted: 'Acceptată',
    rejected: 'Respinsă',
    undone: 'Anulată',
    showInTranscript: 'Arată în transcriere',
    actionFailed: 'Modificarea nu a putut fi salvată. Reîncarcă ședința și încearcă din nou.',
    safeChangesOnly: 'Numerele, datele și negațiile nu sunt niciodată modificate automat.',
    notesRegenerating: 'Corecturi salvate · notițele se actualizează',
    kindProperNoun: 'Nume',
    kindTerm: 'Termen',
    kindNumber: 'Număr',
    kindDate: 'Dată',
    kindNegation: 'Negație',
    kindWording: 'Formulare',
    kindOther: 'Altele',
    qualityTitle: 'Calitatea transcrierii',
    provenance: 'Cuvinte de la {words}. Vorbitori de la {speakers}.',
    provenanceWords: 'Cuvinte de la {words}.',
    coverageComplete: 'Fiecare fereastră de cinci minute a înregistrării are cuvinte.',
    coverageFlagged:
      'Ferestre de cinci minute fără cuvinte: {count}. Ascultă-le înainte să te bazezi pe transcriere.',
    coverageWindow: 'Niciun cuvânt între {start} și {end}',
    repeatedPhrases: 'Expresii repetate marcate ca posibile halucinații: {count}.',
    uncertainSpans: 'Pasaje incerte marcate, nu ghicite: {count}.',
    uncertain: 'Incert',
    uncertainDisagree: 'Motoarele nu sunt de acord aici',
    uncertainLowConfidence: 'Prea incert pentru a sugera o corectură',
    uncertainLong: 'Divergență prea lungă pentru verificare',
    uncertainUnchecked: 'Neverificat',
    disagreements: 'Pasaje în care cele două motoare au diferit: {count}.',
  },
  sk: {
    repairTitle: 'Kontrola prepisu',
    repairRunning: 'Kontrolujem prepis pomocou druhého systému a znalostí firmy…',
    repairFailed: 'Kontrolu prepisu sa nepodarilo dokončiť. Prepis zostal bez zmeny.',
    repairSkipped: 'Tento prepis nebol overený druhým systémom.',
    repairCurrent: 'Prepis je skontrolovaný.',
    appliedCount: 'Použité opravy: {count}',
    suggestedCount: 'Návrhy na kontrolu: {count}',
    noSuggestions: 'Opravy nie sú potrebné.',
    reviewSuggestions: 'Skontrolovať návrhy',
    hideSuggestions: 'Skryť návrhy',
    autoApplied: 'Použité automaticky',
    suggestion: 'Návrh',
    heard: 'Počuté',
    corrected: 'Opravené',
    reason: 'Prečo',
    evidence: 'Dôkazy',
    evidenceEngine: 'Druhý systém: „{text}“',
    evidenceVocabulary: 'Firemný slovník: {term}',
    evidenceKnowledge: 'Znalosti firmy: „{excerpt}“',
    evidenceContext: 'Kontext: „{excerpt}“',
    confidence: 'Istota {percent} %',
    accept: 'Prijať',
    reject: 'Odmietnuť',
    undo: 'Vrátiť',
    accepted: 'Prijaté',
    rejected: 'Odmietnuté',
    undone: 'Vrátené',
    showInTranscript: 'Zobraziť v prepise',
    actionFailed: 'Zmenu sa nepodarilo uložiť. Znova načítajte stretnutie a skúste to znova.',
    safeChangesOnly: 'Čísla, dátumy a zápory sa nikdy nemenia automaticky.',
    notesRegenerating: 'Opravy uložené · poznámky sa obnovujú',
    kindProperNoun: 'Meno',
    kindTerm: 'Termín',
    kindNumber: 'Číslo',
    kindDate: 'Dátum',
    kindNegation: 'Zápor',
    kindWording: 'Formulácia',
    kindOther: 'Iné',
    qualityTitle: 'Kvalita prepisu',
    provenance: 'Slová z {words}. Hovoriaci z {speakers}.',
    provenanceWords: 'Slová z {words}.',
    coverageComplete: 'Každý päťminútový úsek nahrávky obsahuje slová.',
    coverageFlagged:
      'Päťminútové úseky bez slov: {count}. Vypočujte si ich skôr, než sa na prepis spoľahnete.',
    coverageWindow: 'Žiadne slová od {start} do {end}',
    repeatedPhrases: 'Opakované frázy označené ako možné halucinácie: {count}.',
    uncertainSpans: 'Neisté pasáže sú označené, nie hádané: {count}.',
    uncertain: 'Neisté',
    uncertainDisagree: 'Systémy sa tu nezhodujú',
    uncertainLowConfidence: 'Príliš neisté na návrh opravy',
    uncertainLong: 'Príliš dlhý rozpor na kontrolu',
    uncertainUnchecked: 'Neskontrolované',
    disagreements: 'Pasáže, v ktorých sa systémy rozchádzali: {count}.',
  },
  sl: {
    repairTitle: 'Preverjanje prepisa',
    repairRunning: 'Prepis preverjam z drugim sistemom in znanjem podjetja …',
    repairFailed: 'Preverjanja prepisa ni bilo mogoče dokončati. Prepis je nespremenjen.',
    repairSkipped: 'Tega prepisa ni preveril drugi sistem.',
    repairCurrent: 'Prepis je preverjen.',
    appliedCount: 'Uporabljeni popravki: {count}',
    suggestedCount: 'Predlogi za pregled: {count}',
    noSuggestions: 'Popravki niso potrebni.',
    reviewSuggestions: 'Preglej predloge',
    hideSuggestions: 'Skrij predloge',
    autoApplied: 'Uporabljeno samodejno',
    suggestion: 'Predlog',
    heard: 'Slišano',
    corrected: 'Popravljeno',
    reason: 'Zakaj',
    evidence: 'Dokazi',
    evidenceEngine: 'Drugi sistem: »{text}«',
    evidenceVocabulary: 'Besednjak podjetja: {term}',
    evidenceKnowledge: 'Znanje podjetja: »{excerpt}«',
    evidenceContext: 'Kontekst: »{excerpt}«',
    confidence: 'Gotovost {percent} %',
    accept: 'Sprejmi',
    reject: 'Zavrni',
    undo: 'Razveljavi',
    accepted: 'Sprejeto',
    rejected: 'Zavrnjeno',
    undone: 'Razveljavljeno',
    showInTranscript: 'Pokaži v prepisu',
    actionFailed: 'Spremembe ni bilo mogoče shraniti. Znova naložite sestanek in poskusite znova.',
    safeChangesOnly: 'Števila, datumi in zanikanja se nikoli ne spremenijo samodejno.',
    notesRegenerating: 'Popravki shranjeni · zapiski se osvežujejo',
    kindProperNoun: 'Ime',
    kindTerm: 'Izraz',
    kindNumber: 'Število',
    kindDate: 'Datum',
    kindNegation: 'Zanikanje',
    kindWording: 'Ubeseditev',
    kindOther: 'Drugo',
    qualityTitle: 'Kakovost prepisa',
    provenance: 'Besede iz {words}. Govorci iz {speakers}.',
    provenanceWords: 'Besede iz {words}.',
    coverageComplete: 'Vsako petminutno okno posnetka vsebuje besede.',
    coverageFlagged:
      'Petminutna okna brez besed: {count}. Poslušajte jih, preden se zanesete na prepis.',
    coverageWindow: 'Ni besed od {start} do {end}',
    repeatedPhrases: 'Ponavljajoče se fraze, označene kot možne halucinacije: {count}.',
    uncertainSpans: 'Negotovi odlomki so označeni, ne ugibani: {count}.',
    uncertain: 'Negotovo',
    uncertainDisagree: 'Sistema se tu ne strinjata',
    uncertainLowConfidence: 'Preveč negotovo za predlog popravka',
    uncertainLong: 'Predolgo neujemanje za preverjanje',
    uncertainUnchecked: 'Ni preverjeno',
    disagreements: 'Odlomki, v katerih sta se sistema razlikovala: {count}.',
  },
  es: {
    repairTitle: 'Revisión de la transcripción',
    repairRunning:
      'Revisando la transcripción con un segundo motor y el conocimiento de la empresa…',
    repairFailed:
      'No se pudo completar la revisión de la transcripción. La transcripción no ha cambiado.',
    repairSkipped: 'Esta transcripción no se ha contrastado con un segundo motor.',
    repairCurrent: 'Transcripción revisada.',
    appliedCount: 'Correcciones aplicadas: {count}',
    suggestedCount: 'Sugerencias por revisar: {count}',
    noSuggestions: 'No hacen falta correcciones.',
    reviewSuggestions: 'Revisar sugerencias',
    hideSuggestions: 'Ocultar sugerencias',
    autoApplied: 'Aplicada automáticamente',
    suggestion: 'Sugerencia',
    heard: 'Oído',
    corrected: 'Corregido',
    reason: 'Por qué',
    evidence: 'Pruebas',
    evidenceEngine: 'Segundo motor: «{text}»',
    evidenceVocabulary: 'Vocabulario de la empresa: {term}',
    evidenceKnowledge: 'Conocimiento de la empresa: «{excerpt}»',
    evidenceContext: 'Contexto: «{excerpt}»',
    confidence: '{percent} % de seguridad',
    accept: 'Aceptar',
    reject: 'Rechazar',
    undo: 'Deshacer',
    accepted: 'Aceptada',
    rejected: 'Rechazada',
    undone: 'Deshecha',
    showInTranscript: 'Mostrar en la transcripción',
    actionFailed: 'No se pudo guardar el cambio. Vuelve a cargar la reunión e inténtalo de nuevo.',
    safeChangesOnly: 'Los números, las fechas y las negaciones nunca se cambian automáticamente.',
    notesRegenerating: 'Correcciones guardadas · actualizando las notas',
    kindProperNoun: 'Nombre',
    kindTerm: 'Término',
    kindNumber: 'Número',
    kindDate: 'Fecha',
    kindNegation: 'Negación',
    kindWording: 'Redacción',
    kindOther: 'Otro',
    qualityTitle: 'Calidad de la transcripción',
    provenance: 'Palabras de {words}. Hablantes de {speakers}.',
    provenanceWords: 'Palabras de {words}.',
    coverageComplete: 'Cada ventana de cinco minutos de la grabación tiene palabras.',
    coverageFlagged:
      'Ventanas de cinco minutos sin palabras: {count}. Escúchalas antes de fiarte de la transcripción.',
    coverageWindow: 'Sin palabras de {start} a {end}',
    repeatedPhrases: 'Frases repetidas marcadas como posibles alucinaciones: {count}.',
    uncertainSpans: 'Pasajes dudosos, marcados y no adivinados: {count}.',
    uncertain: 'Dudoso',
    uncertainDisagree: 'Los motores no coinciden aquí',
    uncertainLowConfidence: 'Demasiado dudoso para sugerir una corrección',
    uncertainLong: 'Discrepancia demasiado larga para revisarla',
    uncertainUnchecked: 'Sin revisar',
    disagreements: 'Pasajes en los que los dos motores discreparon: {count}.',
  },
  sv: {
    repairTitle: 'Transkriptkontroll',
    repairRunning: 'Kontrollerar transkriptet mot en andra motor och företagets kunskap…',
    repairFailed: 'Kontrollen av transkriptet kunde inte slutföras. Transkriptet är oförändrat.',
    repairSkipped: 'Det här transkriptet har inte kontrollerats mot en andra motor.',
    repairCurrent: 'Transkriptet är kontrollerat.',
    appliedCount: 'Rättelser gjorda: {count}',
    suggestedCount: 'Förslag att granska: {count}',
    noSuggestions: 'Inga rättelser behövs.',
    reviewSuggestions: 'Granska förslag',
    hideSuggestions: 'Dölj förslag',
    autoApplied: 'Rättat automatiskt',
    suggestion: 'Förslag',
    heard: 'Hört',
    corrected: 'Rättat',
    reason: 'Varför',
    evidence: 'Belägg',
    evidenceEngine: 'Andra motorn: ”{text}”',
    evidenceVocabulary: 'Företagets ordlista: {term}',
    evidenceKnowledge: 'Företagets kunskap: ”{excerpt}”',
    evidenceContext: 'Sammanhang: ”{excerpt}”',
    confidence: '{percent} % säker',
    accept: 'Godkänn',
    reject: 'Avvisa',
    undo: 'Ångra',
    accepted: 'Godkänd',
    rejected: 'Avvisad',
    undone: 'Ångrad',
    showInTranscript: 'Visa i transkriptet',
    actionFailed: 'Ändringen kunde inte sparas. Ladda om mötet och försök igen.',
    safeChangesOnly: 'Tal, datum och negationer ändras aldrig automatiskt.',
    notesRegenerating: 'Rättelser sparade · anteckningarna uppdateras',
    kindProperNoun: 'Namn',
    kindTerm: 'Begrepp',
    kindNumber: 'Tal',
    kindDate: 'Datum',
    kindNegation: 'Nekande',
    kindWording: 'Formulering',
    kindOther: 'Annat',
    qualityTitle: 'Transkriptets kvalitet',
    provenance: 'Ord från {words}. Talare från {speakers}.',
    provenanceWords: 'Ord från {words}.',
    coverageComplete: 'Varje femminutersfönster i inspelningen har ord.',
    coverageFlagged:
      'Femminutersfönster utan ord: {count}. Lyssna på dem innan du litar på transkriptet.',
    coverageWindow: 'Inga ord från {start} till {end}',
    repeatedPhrases: 'Upprepade fraser markerade som möjliga hallucinationer: {count}.',
    uncertainSpans: 'Osäkra passager, markerade och inte gissade: {count}.',
    uncertain: 'Osäkert',
    uncertainDisagree: 'Motorerna är oense här',
    uncertainLowConfidence: 'För osäkert för att föreslå en rättelse',
    uncertainLong: 'För lång oenighet för att kontrollera',
    uncertainUnchecked: 'Inte kontrollerat',
    disagreements: 'Passager där motorerna var oense: {count}.',
  },
};
export default meetingsRepairCopy;
